import { generateBase58Id } from './id';

const IS_TEST = import.meta.env.VITE_STRIPE_TEST_MODE === 'true';

export type SubscriptionId = 'monthly' | 'quarterly' | 'yearly' | '72h-unlimited';
export type SubscriptionPeriod = 'monthly' | 'quarterly' | 'yearly' | '72h';

export interface SubscriptionPlan {
  id: SubscriptionId;
  period: SubscriptionPeriod;
  name: string;
  months: number;
  price: number;
  originalPrice: number;
  discount: number;
  discountedPrice: number;
  billedAmount: number;
  hasMonthlyMultiplePeriod: boolean;
  isSubscription: true;
  monthlyGems: number;
  oneTimeGems?: number;
  googlePlaySku?: string;
  appleStoreSku?: string;
  stripe: {
    priceId: string;
    testPriceId: string;
    paymentLink: string;
    testPaymentLink: string;
    promoCode: string;
  };
}

export interface GemPackage {
  id: string;
  gems: number;
  price: number;
  bonus?: number; // gems bonus percentage
  gemsWithoutBonus?: number;
  originalPrice?: number;
  isSubscription?: false;
  appleStoreSku?: string;
  googlePlaySku?: string;
  stripe: {
    priceId: string;
    testPriceId: string;
    paymentLink: string;
    testPaymentLink: string;
  };
}

export type PurchaseOption = SubscriptionPlan | GemPackage;

// Define subscription periods
export const SUBSCRIPTION_PERIODS: Record<SubscriptionPeriod, number> = {
  monthly: 1,
  quarterly: 3,
  yearly: 12,
  '72h': 0,
};

export const GEM_COST = {
  PER_IMAGE: 3,
  PER_VIDEO: 15,
  PER_TTS: 0.3,
};

export const STRIPE_PAYEMENT_BASE_URL = (import.meta.env.VITE_STRIPE_PAYMENT_BASE_URL || 'https://buy.stripe.com')
  .replace(/\/$/, '');

// Define subscription plans
export const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    id: 'monthly',
    period: 'monthly',
    name: '1 Month',
    months: 1,
    price: 12.99,
    originalPrice: 19.99,
    discount: 35,
    discountedPrice: 12.99,
    billedAmount: 12.99,
    hasMonthlyMultiplePeriod: true,
    isSubscription: true,
    monthlyGems: 100,
    appleStoreSku: import.meta.env.VITE_APPLE_MONTHLY_SUB_SKU || 'ai.eh.subs.premium.monthly',
    stripe: {
      priceId: import.meta.env.VITE_STRIPE_LIVE_MONTHLY_PRICE || 'price_monthly_live',
      testPriceId: import.meta.env.VITE_STRIPE_TEST_MONTHLY_PRICE || 'price_monthly_test',
      paymentLink: import.meta.env.VITE_STRIPE_LIVE_MONTHLY_LINK || '/monthly_live',
      testPaymentLink: import.meta.env.VITE_STRIPE_TEST_MONTHLY_LINK || '/test/monthly_test',
      promoCode: import.meta.env.VITE_STRIPE_MONTHLY_COUPON || '',
    },
  },
  {
    id: 'quarterly',
    period: 'quarterly',
    name: '3 Months',
    months: 3,
    price: 9.99,
    originalPrice: 19.99,
    discount: 50,
    discountedPrice: 9.99,
    billedAmount: 29.99, // 3 * 19.99 * 0.5
    hasMonthlyMultiplePeriod: true,
    isSubscription: true,
    monthlyGems: 100,
    appleStoreSku: import.meta.env.VITE_APPLE_QUARTERLY_SUB_SKU || 'ai.eh.subs.premium.quarterly',
    stripe: {
      priceId: import.meta.env.VITE_STRIPE_LIVE_QUARTERLY_PRICE || 'price_quarterly_live',
      testPriceId: import.meta.env.VITE_STRIPE_TEST_QUARTERLY_PRICE || 'price_quarterly_test',
      paymentLink: import.meta.env.VITE_STRIPE_LIVE_QUARTERLY_LINK || '/quarterly_live',
      testPaymentLink: import.meta.env.VITE_STRIPE_TEST_QUARTERLY_LINK || '/test/quarterly_test',
      promoCode: import.meta.env.VITE_STRIPE_QUARTERLY_COUPON || '',
    },
  },
  {
    id: 'yearly',
    period: 'yearly',
    name: '12 Months',
    months: 12,
    price: 5.99,
    originalPrice: 19.99,
    discount: 70,
    discountedPrice: 5.99,
    billedAmount: 71.96, // 12 * 19.99 * (1 - 0.7)
    hasMonthlyMultiplePeriod: true,
    isSubscription: true,
    monthlyGems: 100,
    appleStoreSku: import.meta.env.VITE_APPLE_YEARLY_SUB_SKU || 'ai.eh.subs.premium.yearly',
    stripe: {
      priceId: import.meta.env.VITE_STRIPE_LIVE_YEARLY_PRICE || 'price_yearly_live',
      testPriceId: import.meta.env.VITE_STRIPE_TEST_YEARLY_PRICE || 'price_yearly_test',
      paymentLink: import.meta.env.VITE_STRIPE_LIVE_YEARLY_LINK || '/yearly_live',
      testPaymentLink: import.meta.env.VITE_STRIPE_TEST_YEARLY_LINK || '/test/yearly_test',
      promoCode: import.meta.env.VITE_STRIPE_YEARLY_COUPON || '',
    },
  },
  // Unlimited
  {
    id: '72h-unlimited',
    period: '72h',
    name: '72h',
    months: 0,
    price: 9.99,
    originalPrice: 19.99,
    discount: 50,
    discountedPrice: 9.99,
    billedAmount: 9.99,
    hasMonthlyMultiplePeriod: false,
    isSubscription: true,
    monthlyGems: 0,
    oneTimeGems: 50,
    googlePlaySku: import.meta.env.VITE_PLAY_72H_UNLIMITED_SKU,
    appleStoreSku: import.meta.env.VITE_APPLE_72H_UNLIMITED_SKU,
    stripe: {
      priceId: import.meta.env.VITE_STRIPE_LIVE_72H_UNLIMITED_PRICE || 'price_72h_unlimited_live',
      testPriceId: import.meta.env.VITE_STRIPE_TEST_72H_UNLIMITED_PRICE || 'price_72h_unlimited_test',
      paymentLink: import.meta.env.VITE_STRIPE_LIVE_72H_UNLIMITED_LINK || '/72h_unlimited_live',
      testPaymentLink: import.meta.env.VITE_STRIPE_TEST_72H_UNLIMITED_LINK || '/test/72h_unlimited_test',
      promoCode: '',
    },
  },
];

// Get default pricing or discounted pricing based on env variable
export const getSubscriptionPlans = (): SubscriptionPlan[] => {
  // Check if discounts are enabled
  const discountsEnabled = import.meta.env.VITE_EH_DISCOUNTS === 'true';
  
  if (!discountsEnabled) {
    // Return regular pricing (no discounts)
    return SUBSCRIPTION_PLANS.map(plan => ({
      ...plan,
      price: plan.originalPrice,
      discount: 0,
      billedAmount: plan.originalPrice * plan.months,
    }));
  }
  
  return SUBSCRIPTION_PLANS;
};

// Define gem packages
export const GEM_PACKAGES: GemPackage[] = [
  {
    id: 'xs',
    gems: 100,
    price: 9.99,
    appleStoreSku: import.meta.env.VITE_APPLE_GEMS_XS_SKU || 'ai.eh.gems.xs',
    googlePlaySku: import.meta.env.VITE_PLAY_GEMS_XS_SKU || 'ai.eveninghoney.www.t.gems.xs',
    stripe: {
      priceId: import.meta.env.VITE_STRIPE_LIVE_GEMS_XS_PRICE || 'price_gems_xs_live',
      testPriceId: import.meta.env.VITE_STRIPE_TEST_GEMS_XS_PRICE || 'price_gems_xs_test',
      paymentLink: import.meta.env.VITE_STRIPE_LIVE_GEMS_XS_LINK || '/gems_xs_live',
      testPaymentLink: import.meta.env.VITE_STRIPE_TEST_GEMS_XS_LINK || '/test/gems_xs_test',
    },
  },
  {
    id: 's',
    gems: 250,
    bonus: 0.04,
    gemsWithoutBonus: 240,
    price: 23.99,
    appleStoreSku: import.meta.env.VITE_APPLE_GEMS_S_SKU || 'ai.eh.gems.s',
    googlePlaySku: import.meta.env.VITE_PLAY_GEMS_S_SKU || 'ai.eveninghoney.www.t.gems.s',
    stripe: {
      priceId: import.meta.env.VITE_STRIPE_LIVE_GEMS_S_PRICE || 'price_gems_s_live',
      testPriceId: import.meta.env.VITE_STRIPE_TEST_GEMS_S_PRICE || 'price_gems_s_test',
      paymentLink: import.meta.env.VITE_STRIPE_LIVE_GEMS_S_LINK || '/gems_s_live',
      testPaymentLink: import.meta.env.VITE_STRIPE_TEST_GEMS_S_LINK || '/test/gems_s_test',
    },
  },
  {
    id: 'm',
    gems: 550,
    bonus: 0.1,
    gemsWithoutBonus: 500,
    price: 49.99,
    appleStoreSku: import.meta.env.VITE_APPLE_GEMS_M_SKU || 'ai.eh.gems.m',
    googlePlaySku: import.meta.env.VITE_PLAY_GEMS_M_SKU || 'ai.eveninghoney.www.t.gems.m',
    stripe: {
      priceId: import.meta.env.VITE_STRIPE_LIVE_GEMS_M_PRICE || 'price_gems_m_live',
      testPriceId: import.meta.env.VITE_STRIPE_TEST_GEMS_M_PRICE || 'price_gems_m_test',
      paymentLink: import.meta.env.VITE_STRIPE_LIVE_GEMS_M_LINK || '/gems_m_live',
      testPaymentLink: import.meta.env.VITE_STRIPE_TEST_GEMS_M_LINK || '/test/gems_m_test',
    },
  },
  {
    id: 'l',
    gems: 1150,
    bonus: 0.15,
    gemsWithoutBonus: 1000,
    price: 99.99,
    appleStoreSku: import.meta.env.VITE_APPLE_GEMS_L_SKU || 'ai.eh.gems.l',
    googlePlaySku: import.meta.env.VITE_PLAY_GEMS_L_SKU || 'ai.eveninghoney.www.t.gems.l',
    stripe: {
      priceId: import.meta.env.VITE_STRIPE_LIVE_GEMS_L_PRICE || 'price_gems_l_live',
      testPriceId: import.meta.env.VITE_STRIPE_TEST_GEMS_L_PRICE || 'price_gems_l_test',
      paymentLink: import.meta.env.VITE_STRIPE_LIVE_GEMS_L_LINK || '/gems_l_live',
      testPaymentLink: import.meta.env.VITE_STRIPE_TEST_GEMS_L_LINK || '/test/gems_l_test',
    },
  },
  {
    id: 'xl',
    gems: 2400,
    bonus: 0.2,
    gemsWithoutBonus: 2000,
    price: 199.99,
    appleStoreSku: import.meta.env.VITE_APPLE_GEMS_XL_SKU || 'ai.eh.gems.xl',
    googlePlaySku: import.meta.env.VITE_PLAY_GEMS_XL_SKU || 'ai.eveninghoney.www.t.gems.xl',
    stripe: {
      priceId: import.meta.env.VITE_STRIPE_LIVE_GEMS_XL_PRICE || 'price_gems_xl_live',
      testPriceId: import.meta.env.VITE_STRIPE_TEST_GEMS_XL_PRICE || 'price_gems_xl_test',
      paymentLink: import.meta.env.VITE_STRIPE_LIVE_GEMS_XL_LINK || '/gems_xl_live',
      testPaymentLink: import.meta.env.VITE_STRIPE_TEST_GEMS_XL_LINK || '/test/gems_xl_test',
    },
  },
  {
    id: 'xxl',
    gems: 5000,
    bonus: 0.25,
    gemsWithoutBonus: 4000,
    price: 399.99,
    appleStoreSku: import.meta.env.VITE_APPLE_GEMS_XXL_SKU || 'ai.eh.gems.xxl',
    googlePlaySku: import.meta.env.VITE_PLAY_GEMS_XXL_SKU || 'ai.eveninghoney.www.t.gems.xxl',
    stripe: {
      priceId: import.meta.env.VITE_STRIPE_LIVE_GEMS_XXL_PRICE || 'price_gems_xxl_live',
      testPriceId: import.meta.env.VITE_STRIPE_TEST_GEMS_XXL_PRICE || 'price_gems_xxl_test',
      paymentLink: import.meta.env.VITE_STRIPE_LIVE_GEMS_XXL_LINK || '/gems_xxl_live',
      testPaymentLink: import.meta.env.VITE_STRIPE_TEST_GEMS_XXL_LINK || '/test/gems_xxl_test',
    },
  }
];

// Generate a unique order ID for payment tracking
export const generateOrderId = (): string => {
  const timestamp = Date.now().toString().slice(-6);
  const randomChars = generateBase58Id('').split('_')[1].substring(0, 6);
  return `eh-${timestamp}-${randomChars}`;
};

// Get subscription end date based on current date and period
export const getSubscriptionEndDate = (
  startDate: Date,
  period: SubscriptionPeriod
): Date => {
  const endDate = new Date(startDate);
  endDate.setMonth(endDate.getMonth() + SUBSCRIPTION_PERIODS[period]);
  return endDate;
};

// Calculate new end date when extending a subscription
export const getExtendedSubscriptionEndDate = (
  currentEndDate: Date | string,
  period: SubscriptionPeriod
): Date => {
  const endDate = new Date(currentEndDate);
  endDate.setMonth(endDate.getMonth() + SUBSCRIPTION_PERIODS[period]);
  return endDate;
};

// Helper to get the appropriate Stripe configuration based on environment
export const getStripeConfig = (plan: SubscriptionPlan) => {
  return {
    priceId: IS_TEST ? plan.stripe.testPriceId : plan.stripe.priceId,
    // ensure leading slash
    paymentLink: '/' + (IS_TEST ? plan.stripe.testPaymentLink : plan.stripe.paymentLink).replace(/^\//, ''),
    // promoCode takes too much time to be applied on stripe checkout page, degrading conversion rate
    promoCode: plan.stripe.promoCode ?? undefined,
  };
};

// Helper to get the appropriate Stripe configuration for gem packages based on environment
export const getGemStripeConfig = (gemPackage: GemPackage) => {
  return {
    priceId: IS_TEST ? gemPackage.stripe.testPriceId : gemPackage.stripe.priceId,
    // ensure leading slash
    paymentLink: '/' + (IS_TEST ? gemPackage.stripe.testPaymentLink : gemPackage.stripe.paymentLink).replace(/^\//, ''),
  };
};

export const STRIPE_BILLING_BASE_URL = (import.meta.env.VITE_STRIPE_BILLING_BASE_URL || 'https://billing.stripe.com/p')
  .replace(/\/$/, '');

export function getStripeBillingPortalUrl(email?: string) {
  const billingLink = IS_TEST
    ? import.meta.env.VITE_STRIPE_TEST_BILLING_PORTAL_LINK
    : import.meta.env.VITE_STRIPE_LIVE_BILLING_PORTAL_LINK;

  if (!billingLink) return ''

  let url = `${STRIPE_BILLING_BASE_URL}/${billingLink.replace(/^\//, '')}`;
  if (email) {
    url = `${url}?prefilled_email=${encodeURIComponent(email)}`;
  }
  return url;
};